import React, { useState, useEffect, useContext } from 'react';
import styles from './styles.module.scss';
import { Board } from '../../components/Board';
import 'moment/locale/pt';
import { Loading } from '../../components/Loading';
import { User } from '../../types';
import { verifyRoutePermissions, giftToExercises } from '../../globalFunctions';
import { NavButton } from '../../components/NavButton';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadModulesStatus } from '../Trilogic/HubRoute/requestHandlers';
import { ModuleStatus } from '../Trilogic/HubRoute/types';
import { useModule } from '../../hooks/useModule';
import Card from './Excard';
import { destroyExercise } from './requestHandlers';
import { MessageContext } from '../../providers/MessageProvider';
import { AlertDialog, AlertDialogType } from '../../components/AlertDialog';
import { saveExerciseChanges, importBatch } from './requestHandlers';
import ImportGift from '../../components/ImportGift';

export const LogicMaker = () => {
    const [isLoadingPage, setIsLoadingPage] = useState<boolean>(false);
    const [modulesStatus, setModulesStatus] = useState<ModuleStatus[]>();
    const [exerciciosImportados, setExerciciosImportados]: any = useState();
    const boardRef = useRef<HTMLDivElement>(null);
    const [exercises, setExercises] = useState<any>();
    const [moduleSlug, setModuleSlug] = useState<string>(
        sessionStorage.getItem('selectedModule') || ''
    );
    const [userAuth, setUserAuth] = useState<User>({} as User);
    const navigate = useNavigate();
    const moduleData = useModule(moduleSlug);
    const { setOpenMessage, setMessage } = useContext(MessageContext);
    const [alertDialogProps, setAlertDialogProps] = useState<AlertDialogType>();
    const [openAlert, setOpenAlert] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Carrega status dos módulos
    const handleLoadModulesStatus = async () => {
        setIsLoadingPage(true);
        const result = await loadModulesStatus(userAuth.token, navigate);
        setIsLoadingPage(false);

        if (result && !('error' in result)) {
            setModulesStatus(result.modules);
            if (moduleSlug == '') {
                setModuleSlug(result.modules[0].slug);
            }
        }
    };

    // On mount: pega usuário
    useEffect(() => {
        const user = verifyRoutePermissions(
            ['auth', 'email.verified', 'logicmanager.classes'],
            navigate
        );
        if (typeof user === 'object') {
            setUserAuth(user);
        }
    }, [navigate]);

    // Sempre que token mudar, recarrega módulos
    useEffect(() => {
        if (userAuth.token) {
            handleLoadModulesStatus();
        }
    }, [userAuth.token]);

    useEffect(() => {
        if (!moduleData.isLoading && moduleData.data) {
            setExercises(moduleData.data.exercises ?? []);
            setIsLoadingPage(false);
        }
    }, [moduleData.data, moduleData.isLoading]);

    const editarFase = (json: any) => {
        sessionStorage.setItem('jsonEditar', JSON.stringify(json));
        sessionStorage.setItem('action', 'edit');
        navigate('/manager/editor');
    };

    const criarFase = () => {
        sessionStorage.setItem('action', 'criar');
        sessionStorage.setItem('jsonEditar', JSON.stringify({ dropAreas: [], steps: [] }));
        navigate('/manager/editor');
    };

    const deletarFase = async (id: number) => {
        const sucesso = await destroyExercise(userAuth.token, id, navigate);
        setOpenMessage(true);

        if (sucesso) {
            const filtro = Object.values(exercises).filter((ex: any) => ex.id !== id);
            setExercises(filtro);
            setMessage({
                type: 'success',
                content: 'Exercicio deletado com sucesso'
            });
        } else {
            setMessage({
                type: 'error',
                content: 'Erro ao deletar exercicio'
            });
        }
    };

    const ConfirmarDeletar = id => {
        setOpenAlert(true);

        setAlertDialogProps({
            title: 'Deseja deletar esta fase?',
            message: 'Ao deletar esta fase todo o seu histórico de jogadas também será deletado',
            options: [
                {
                    text: 'Cancelar',
                    action: () => {},
                    type: 'success'
                },
                {
                    text: 'Deletar',
                    action: async () => deletarFase(id),
                    type: 'error'
                }
            ]
        });
    };

    const importExercises = async () => {
        const moduleIdStr = sessionStorage.getItem('moduleId');
        const moduleId = moduleIdStr ? Number(moduleIdStr) : 1;
        if (!exerciciosImportados || exerciciosImportados.length === 0) {
            setMessage({ type: 'error', content: 'Nenhum exercício para importar' });
            setOpenMessage(true);
            return;
        }

        setOpenMessage(true);
        setMessage({ type: 'info', content: 'Iniciando importação em lote...' });

        // Monta payload no formato esperado pelo backend
        const exercisesPayload = exerciciosImportados.map(newEx => ({
            FK_module_id: moduleId,
            json: typeof newEx.json === 'string' ? newEx.json : JSON.stringify(newEx.json || {}),
            description: newEx.description || '',
            name: newEx.title || 'Sem título',
            is_active: 1
        }));

        // Chama endpoint de batch (importBatch)
        const result = await importBatch(userAuth.token, exercisesPayload, navigate);

        if (!result) {
            setMessage({
                type: 'error',
                content: 'Erro ao importar exercícios (ver console para detalhes).'
            });
            setExerciciosImportados(undefined);
            return;
        }

        // resultado esperado (conforme controller sugerido): { success: true, inserted: N, ids: [...] }
        if (result && result.success) {
            setMessage({
                type: 'success',
                content: `Importação concluída: ${result.inserted} exercícios criados.`
            });
        } else {
            // se backend retornou objeto com detalhes de falha
            setMessage({
                type: 'error',
                content: `Importação retornou erro: ${JSON.stringify(result)}`
            });
        }

        setExerciciosImportados(undefined);
        setOpenMessage(true);
    };

    // handler do input .gift
    const handleGiftUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = () => {
            const giftText = reader.result as string;
            const parsed = giftToExercises(giftText);
            console.log(parsed || 'nil');
            setExerciciosImportados(parsed);

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        };
        reader.readAsText(file, 'UTF-8');
    };

    const publicarExercicio = async (id: number) => {
        setOpenMessage(true);
        try {
            const success = await saveExerciseChanges(
                userAuth.token,
                { id: id, is_active: true },
                navigate
            );
            if (success) {
                setExercises((prev: any) =>
                    Object.values(prev).map((ex: any) =>
                        ex.id === id ? { ...ex, is_active: 1 } : ex
                    )
                );
                setMessage({
                    type: 'success',
                    content: 'Exercicio publicado: '
                });
            } else {
                setMessage({
                    type: 'error',
                    content: 'erro ao publicar exercicio'
                });
            }
        } catch {}
    };

    const PrivarExercicio = async (id: number) => {
        setOpenMessage(true);
        try {
            const success = await saveExerciseChanges(
                userAuth.token,
                { id: id, is_active: false },
                navigate
            );
            if (success) {
                setExercises((prev: any) =>
                    Object.values(prev).map((ex: any) =>
                        ex.id === id ? { ...ex, is_active: 0 } : ex
                    )
                );
                setMessage({
                    type: 'success',
                    content: 'Exercicio privado: '
                });
            } else {
                setMessage({
                    type: 'error',
                    content: 'erro ao privar exercicio'
                });
            }
        } catch {}
    };

    return (
        <div className={styles.wrapper} ref={boardRef}>
            {exerciciosImportados && (
                <ImportGift
                    exercicios={exerciciosImportados}
                    onConfirm={importExercises}
                    onCancel={() => {
                        setExerciciosImportados(undefined);
                    }}
                />
            )}
            <Board wrapperClass={`${styles.board} ${styles.big}`}>
                {!exercises ? (
                    <Loading />
                ) : (
                    <>
                        {alertDialogProps && (
                            <AlertDialog
                                setOpen={setOpenAlert}
                                open={openAlert}
                                title={alertDialogProps.title}
                                message={alertDialogProps.message}
                                options={alertDialogProps.options}
                            />
                        )}
                        <h1 className={styles.titleFases}>Lista de fases</h1>

                        <label
                            style={{
                                padding: '0.5rem 1rem',
                                background: '#28a745',
                                color: 'white',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                position: 'absolute',
                                width: '20vw',
                                textAlign: 'center',
                                left: '5vw'
                            }}
                        >
                            Importar banco de questões
                            <input
                                ref={fileInputRef}
                                type='file'
                                accept='.gift,.txt'
                                onChange={handleGiftUpload}
                                style={{ display: 'none' }}
                            />
                        </label>

                        <select
                            value={moduleSlug}
                            onChange={e => {
                                setModuleSlug(e.target.value);
                                const modulo = modulesStatus?.find(
                                    module => module.slug === e.target.value
                                );
                                if (modulo) {
                                    sessionStorage.setItem('moduleId', modulo.id.toString());
                                }
                                sessionStorage.setItem('selectedModule', e.target.value);
                            }}
                            className={styles.selectCustom}
                        >
                            {modulesStatus?.map(module => (
                                <option value={module.slug} key={module.slug}>
                                    {module.name}
                                </option>
                            ))}
                        </select>

                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                overflowY: 'auto',
                                height: '73%'
                            }}
                        >
                            {exercises &&
                                Object.values(exercises).map((ex: any) => (
                                    <Card
                                        key={ex.id}
                                        name={ex.name}
                                        id={ex.id}
                                        destroy={ConfirmarDeletar}
                                        editar={() => {
                                            editarFase(ex);
                                        }}
                                        publico={ex.is_active}
                                        publicar={() => {
                                            publicarExercicio(ex.id);
                                        }}
                                        privar={() => {
                                            PrivarExercicio(ex.id);
                                        }}
                                    />
                                ))}

                            <div
                                onClick={criarFase}
                                className={styles.card}
                                style={{
                                    width: '12vw',
                                    height: '12vw',
                                    cursor: 'pointer',
                                    borderColor: 'green',
                                    color: 'green',
                                    fontSize: '5vw',
                                    fontWeight: '900',
                                    textShadow: `
                  -2px -2px 0 green,
                   2px -2px 0 green,
                  -2px  2px 0 green,
                   2px  2px 0 green
                `
                                }}
                            >
                                +
                            </div>
                        </div>
                    </>
                )}
            </Board>

            <NavButton
                direction='left'
                style={{ left: '1vw', top: '2vw', zIndex: '20' }}
                onClick={() => navigate('/manager')}
            />
        </div>
    );
};
